import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, User, Mail, Lock, Eye, EyeOff, Calendar, MapPin, Phone,
  Plus, Trash2, CheckCircle, Loader2, Heart, ShieldCheck, Edit2, Wallet,
  ChevronUp, ChevronDown, Info,
} from "lucide-react";
import GoogleAuthButton from "../shop/GoogleAuthButton";
import { usePaymentCharges } from "../../hooks/usePaymentCharges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Steps — password removed for new members (set on first login after approval)
const NEW_STEPS = ["Personal Details", "Verify Email", "Family Members", "Agreements", "Payment"];
const UPGRADE_STEPS = ["Your Details", "Family Members", "Agreements", "Payment"];

// Module-level constants
const TODAY = new Date().toISOString().split("T")[0];
const MIN_DOB = new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split("T")[0];

// ── Helper functions ──────────────────────────────────────────────────────────

const calculateAge = (dob) => {
  if (!dob) return 0;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const validateDob = (dob) => {
  if (!dob) return "Date of birth is required";
  const birth = new Date(dob);
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  if (birth >= todayDate) return "Date of birth cannot be in the future";
  if (calculateAge(dob) > 120) return "Please enter a valid date of birth";
  return null;
};

const getErrMsg = (err, fallback = "Something went wrong") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  return fallback;
};

// ── Shared layout components ──────────────────────────────────────────────────

const StepIndicator = ({ visual, steps }) => (
  <div className="flex items-center justify-between mb-8 overflow-x-auto pb-1">
    {steps.map((_, i) => (
      <div key={i} className="flex items-center">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          i + 1 < visual ? "bg-green-500 text-white"
          : i + 1 === visual ? "bg-[#1e3a5f] text-white"
          : "bg-slate-200 text-slate-400"
        }`}>
          {i + 1 < visual ? "\u2713" : i + 1}
        </div>
        {i < steps.length - 1 && (
          <div className={`w-6 h-0.5 mx-0.5 flex-shrink-0 ${i + 1 < visual ? "bg-green-500" : "bg-slate-200"}`} />
        )}
      </div>
    ))}
  </div>
);

const Card = ({ children, visual, steps, onBack, isStep1 }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-[#2d5a8f] flex items-start justify-center px-4 py-8">
    <div className="w-full max-w-md">
      <button onClick={onBack} className="mb-5 flex items-center gap-2 text-white/70 hover:text-white transition-colors">
        <ArrowLeft className="w-5 h-5" />
        <span className="text-sm">{isStep1 ? "Back to portal" : "Previous step"}</span>
      </button>
      <div className="bg-white rounded-3xl p-6 shadow-xl">
        {visual < 8 && <StepIndicator visual={visual} steps={steps} />}
        {children}
      </div>
    </div>
  </div>
);

// ── Search & Link existing member ────────────────────────────────────────────

const SearchLinkForm = ({ onLinked, onCancel, mainAddress, mainMobile }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState(null);
  const [consentCode, setConsentCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSearch = async () => {
    if (query.length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/members/search-public?q=${encodeURIComponent(query)}`);
      setResults(res.data || []);
      setHasSearched(true);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const handleSendConsent = async (person) => {
    // iter141: `person` may be a member OR a guest. `kind` routes the backend
    // lookup and, on verify, tells the frontend whether to link as-is (member)
    // or trigger an upgrade card (guest).
    const kind = person.type === "guest" ? "guest" : "member";
    const personId = kind === "guest" ? person.guest_id : person.member_id;
    setSelected({ ...person, __kind: kind });
    setVerifying(true);
    try {
      await axios.post(`${API}/members/send-link-consent-public`, {
        person_id: personId,
        kind,
      });
      setCodeSent(true);
      toast.success(`Consent code sent to ${person.name}'s email`);
    } catch (err) {
      toast.error(getErrMsg(err, "Failed to send consent code"));
    } finally { setVerifying(false); }
  };

  const handleVerifyConsent = async () => {
    if (!consentCode) return toast.error("Enter the consent code");
    const kind = selected?.__kind || "member";
    const personId = kind === "guest" ? selected.guest_id : selected.member_id;
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/members/verify-link-consent-public`, {
        person_id: personId,
        kind,
        code: consentCode,
      });
      if (kind === "guest") {
        const g = res.data.guest || {};
        toast.success(`${g.name || "Guest"} linked — will be upgraded to family member on submit.`);
        onLinked({
          name: g.name || selected.name,
          email: g.email || selected.email,
          dob: g.dob || selected.dob || "",
          address: g.address || mainAddress || "",
          mobile_number: g.mobile_number || mainMobile || "",
          email_verified: true,       // guest was OTP-verified via consent
          linked_guest_id: g.guest_id || selected.guest_id,
          linked: true,
          linked_type: "guest",
          inherited_address: !g.address && !!mainAddress,
          inherited_mobile: !g.mobile_number && !!mainMobile,
        });
        return;
      }
      // Member path
      toast.success(`${res.data.member?.name || "Member"} linked!`);
      onLinked({
        member_id: selected.member_id,
        name: res.data.member?.name || selected.name,
        email: res.data.member?.email || selected.email,
        dob: res.data.member?.dob || selected.dob || "",
        address: res.data.member?.address || selected.address || "",
        mobile_number: res.data.member?.mobile_number || selected.mobile_number || "",
        last_membership_fee_paid_date: res.data.member?.last_membership_fee_paid_date || null,
        linked: true,
        linked_type: "member",
      });
    } catch (err) {
      toast.error(getErrMsg(err, "Verification failed"));
    } finally { setVerifying(false); }
  };

  return (
    <div className="bg-blue-50 rounded-xl p-4 mb-3 space-y-3">
      <h3 className="font-semibold text-sm text-[#1e3a5f]">Link Existing Member / Guest</h3>
      <p className="text-xs text-slate-500">Search by name or email to link an existing Maitree member or guest to your family.</p>

      {!codeSent ? (
        <>
          <div className="flex gap-2">
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search by name or email..."
              className="flex-1 px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
              data-testid="link-search-input"
            />
            <button
              onClick={handleSearch}
              disabled={searching || query.length < 2}
              className="px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium disabled:opacity-50"
              data-testid="link-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </button>
          </div>
          {results.length > 0 && (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {results.map(r => {
                const isGuest = r.type === "guest";
                const rowKey = isGuest ? `g-${r.guest_id}` : r.member_id;
                return (
                <div key={rowKey} className="bg-white rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                      {r.name}
                      {isGuest && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid={`guest-badge-${r.guest_id}`}>
                          {"Guest \u00b7 needs upgrade"}
                        </span>
                      )}
                      {!isGuest && r.already_in_family && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 font-semibold"
                          title="This member is already part of another family; linking will move them to yours."
                          data-testid={`in-family-badge-${r.member_id}`}
                        >
                          Already in a family
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-400">{r.email}</p>
                  </div>
                  {isGuest ? (
                    <button
                      onClick={() => handleSendConsent(r)}
                      disabled={verifying}
                      className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium disabled:opacity-50"
                      data-testid={`guest-upgrade-link-${r.guest_id}`}
                    >
                      Send Consent
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSendConsent(r)}
                      disabled={verifying}
                      className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50"
                      data-testid={`link-select-${r.member_id}`}
                    >
                      Send Consent
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {results.length === 0 && hasSearched && !searching && (
            <div
              className="rounded-lg bg-white border border-slate-200 px-3 py-2.5 text-center"
              data-testid="link-search-no-results"
            >
              <p className="text-xs font-semibold text-slate-600">No matching members or guests found</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Try a different name or email, or add them as a new family member below.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-3">
            <p className="text-sm font-medium">{selected?.name}</p>
            <p className="text-xs text-slate-400">A consent code has been sent to their email.</p>
          </div>
          <div className="flex gap-2">
            <input
              value={consentCode}
              onChange={e => setConsentCode(e.target.value)}
              placeholder="Enter 6-digit consent code"
              maxLength={6}
              className="flex-1 px-3 py-2.5 rounded-lg border text-sm font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
              data-testid="link-consent-code-input"
            />
            <button
              onClick={handleVerifyConsent}
              disabled={verifying}
              className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-50"
              data-testid="link-verify-consent-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
            </button>
          </div>
        </div>
      )}
      <button onClick={onCancel} className="w-full py-2 rounded-lg bg-slate-200 text-slate-700 font-semibold text-sm">Cancel</button>
    </div>
  );
};


// ── Family Form with inline email verification ──────────────────────────────

const FamilyForm = ({ data, setData, onConfirm, onCancel, confirmLabel, mainAddress }) => {
  const [verifying, setVerifying] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(data.email_verified || false);

  const isAdult = calculateAge(data.dob) >= 17;
  const emailRequired = isAdult;
  const emailProvided = data.email && data.email.trim().length > 0;
  const needsVerification = emailProvided && !codeVerified;

  const handleSendCode = async () => {
    if (!data.email) return toast.error("Please enter an email address");
    setVerifying(true);
    try {
      await axios.post(`${API}/members/family/send-verification`, {
        email: data.email,
        name: data.name || "Family Member",
      });
      setCodeSent(true);
      toast.success(`Verification code sent to ${data.email}`);
    } catch (err) {
      toast.error(getErrMsg(err, "Failed to send verification code"));
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyCode) return toast.error("Please enter the code");
    setVerifying(true);
    try {
      await axios.post(`${API}/members/family/verify-email`, {
        email: data.email,
        code: verifyCode,
      });
      setCodeVerified(true);
      setData({ ...data, email_verified: true });
      toast.success("Email verified!");
    } catch (err) {
      toast.error(getErrMsg(err, "Verification failed"));
    } finally {
      setVerifying(false);
    }
  };

  // Reset verification state when email changes
  const handleEmailChange = (val) => {
    setData({ ...data, email: val, email_verified: false });
    setCodeVerified(false);
    setCodeSent(false);
    setVerifyCode("");
  };

  // Pre-fill address from main member if empty
  useEffect(() => {
    if (!data.address && mainAddress) {
      setData(prev => ({ ...prev, address: mainAddress }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canConfirm = () => {
    if (!data.name || !data.dob) return false;
    if (emailRequired && !emailProvided) return false;
    if (emailProvided && !codeVerified) return false;
    return true;
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 mb-3 space-y-3">
      <h3 className="font-semibold text-sm text-[#1e3a5f]">
        {confirmLabel === "Add" ? "Add Family Member" : "Edit Family Member"}
      </h3>
      <input
        value={data.name}
        onChange={e => setData({ ...data, name: e.target.value })}
        placeholder="Full name *"
        className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
        data-testid="family-name-input"
      />
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Date of birth *</label>
        <input
          value={data.dob}
          onChange={e => setData({ ...data, dob: e.target.value })}
          type="date"
          max={TODAY}
          min={MIN_DOB}
          className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
          data-testid="family-dob-input"
        />
        {data.dob && (
          <p className="text-xs text-slate-400 mt-1">
            {isAdult ? "Adult (17+) — email required" : "Child — email optional"}
          </p>
        )}
      </div>

      {/* Email with inline verification */}
      <div>
        <label className="text-xs text-slate-500 mb-1 block">
          Email {emailRequired ? "*" : "(optional)"}
        </label>
        <div className="flex gap-2">
          <input
            value={data.email || ""}
            onChange={e => handleEmailChange(e.target.value)}
            type="email"
            placeholder={emailRequired ? "Required for adults" : "Optional"}
            disabled={codeVerified}
            className="flex-1 px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] disabled:bg-slate-100"
            data-testid="family-email-input"
          />
          {codeVerified ? (
            <div className="flex items-center px-3 text-green-500">
              <CheckCircle className="w-5 h-5" />
            </div>
          ) : emailProvided && !codeSent ? (
            <button
              type="button"
              onClick={handleSendCode}
              disabled={verifying}
              className="px-3 py-2 rounded-lg bg-[#1e3a5f] text-white text-xs font-medium whitespace-nowrap disabled:opacity-50"
              data-testid="family-send-code-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Code"}
            </button>
          ) : null}
        </div>

        {/* OTP input for family member email */}
        {codeSent && !codeVerified && (
          <div className="flex gap-2 mt-2">
            <input
              value={verifyCode}
              onChange={e => setVerifyCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="flex-1 px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] font-mono tracking-wider"
              data-testid="family-verify-code-input"
            />
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={verifying}
              className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50"
              data-testid="family-verify-code-btn"
            >
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
            </button>
          </div>
        )}
      </div>

      <input
        value={data.mobile_number || ""}
        onChange={e => setData({ ...data, mobile_number: e.target.value })}
        type="tel"
        placeholder="Mobile (optional)"
        className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
      />
      <div>
        <label className="text-xs text-slate-500 mb-1 block">Address</label>
        <textarea
          value={data.address || ""}
          onChange={e => setData({ ...data, address: e.target.value })}
          placeholder="Defaults to main member's address"
          rows={2}
          className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] resize-none"
          data-testid="family-address-input"
        />
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => {
            if (!canConfirm()) {
              if (emailRequired && !emailProvided) return toast.error("Email is required for adult family members");
              if (emailProvided && !codeVerified) return toast.error("Please verify the email address first");
              return toast.error("Name and date of birth are required");
            }
            onConfirm();
          }}
          className={`flex-1 py-2.5 rounded-lg font-semibold text-sm ${canConfirm() ? "bg-[#1e3a5f] text-white" : "bg-slate-300 text-slate-500"}`}
          data-testid="family-form-confirm"
        >
          {confirmLabel}
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-lg bg-slate-200 text-slate-700 font-semibold text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function MemberRegister() {
  const [step, setStep] = useState(1);
  const [memberTypes, setMemberTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  // Existing-guest upgrade (OTP-based, works for password + Google auth)
  const [isExistingGuest, setIsExistingGuest] = useState(false);
  // True when the email was pre-populated from the logged-in guest session
  // (via ?email=… on /member-register). We lock the email input in that case
  // so the user can't accidentally change their identity mid-upgrade — they
  // are already authenticated as this email.
  const [emailLockedFromSession, setEmailLockedFromSession] = useState(false);
  // Payment audience:
  //   - Guest upgrading → still pays as "guest" until membership is confirmed.
  //   - Brand-new signup → pays as "member".
  // Matches backend behaviour in `members.create_subscription_checkout`.
  const { computeFee: computeStripeFee, label: chargesLabel } = usePaymentCharges(
    isExistingGuest ? "guest" : "member"
  );

  const [guestVerified, setGuestVerified] = useState(false);
  const [guestOtp, setGuestOtp] = useState("");
  const [guestOtpSent, setGuestOtpSent] = useState(false);
  const [guestOtpSending, setGuestOtpSending] = useState(false);

  // Step 1 fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState("");

  // Step 2 – OTP
  const [code, setCode] = useState("");

  // Step 3 – Family members
  const [familyMembers, setFamilyMembers] = useState([]);
  const [newFamily, setNewFamily] = useState({ name: "", email: "", dob: "", mobile_number: "", address: "", email_verified: false });
  const [showAddFamily, setShowAddFamily] = useState(false);
  const [showLinkSearch, setShowLinkSearch] = useState(false);
  const [editFamilyIdx, setEditFamilyIdx] = useState(null);
  const [editFamily, setEditFamily] = useState({ name: "", email: "", dob: "", mobile_number: "", address: "", email_verified: false });

  // Step 4 – auto-assigned (not shown to user)
  const [selectedTypeId, setSelectedTypeId] = useState("");

  // Step 5 – Agreements
  const [satzungConsent, setSatzungConsent] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [autoSubConsent, setAutoSubConsent] = useState(false);
  const [memberPassword, setMemberPassword] = useState("");
  const [showMemberPw, setShowMemberPw] = useState(false);

  // Step 7 – Payment
  const [regToken, setRegToken] = useState(null);
  const [annualFee, setAnnualFee] = useState(0);
  const [memberTypeName, setMemberTypeName] = useState("");
  const [paymentDone, setPaymentDone] = useState(false);
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);
  // SEPA payment
  const [showSepa, setShowSepa] = useState(false);
  const [sepaForm, setSepaForm] = useState({ account_holder: "", street: "", plz_city: "", bank_name: "", iban: "", bic: "" });
  const [sepaLoading, setSepaLoading] = useState(false);
  const [sepaEmail, setSepaEmail] = useState("");
  const [sepaEmailCode, setSepaEmailCode] = useState("");
  const [sepaEmailSent, setSepaEmailSent] = useState(false);
  const [sepaEmailVerified, setSepaEmailVerified] = useState(false);
  const [sepaEmailLoading, setSepaEmailLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  // Google prefill state (arrives from GoogleContinue after Emergent Google auth).
  // Only used to pre-populate email + name — the user still goes through the
  // full normal registration flow including OTP re-verification of the email.
  const googlePrefill = location.state?.google || null;

  // Visual step mapping (step 4 removed, step 6 removed for new members)
  // NEW: steps 1,2,3,[skip4],5,[skip6],7 → visual 1,2,3,4,5
  // UPGRADE: 1→1, 3→2, 5→3, 7→4
  const upgradeStepMap = { 1: 1, 3: 2, 5: 3, 7: 4 };
  const steps = isExistingGuest ? UPGRADE_STEPS : NEW_STEPS;
  const visual = isExistingGuest
    ? (upgradeStepMap[step] ?? step)
    : step <= 3 ? step : step === 5 ? 4 : step === 7 ? 5 : step;

  const handleBack = () => {
    if (step <= 1) return navigate("/" + (window.location.pathname.split("/")[1] || "mymaitree") );
    if (step === 3 && isExistingGuest) return setStep(1);
    if (step === 5) return setStep(3);     // skip removed step 4
    if (step === 7) return setStep(5);     // agreements → payment (skip removed step 6)
    if (step >= 8) return;
    setStep(step - 1);
  };

  const loadMemberTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/members/types/public`);
      setMemberTypes(res.data || []);
    } catch (err) { console.error("Request failed:", err?.message || err); }
  }, []);

  const loadWalletBalance = useCallback(async (token) => {
    const t = token || regToken;
    if (!t) return;
    setWalletLoading(true);
    try {
      const res = await axios.get(`${API}/members/stripe/wallet-balance`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setWalletBalance(res.data.balance || 0);
    } catch (err) { console.error("Request failed:", err?.message || err); }
    finally { setWalletLoading(false); }
  }, [regToken]);

  const pollPaymentStatus = useCallback(async (sessionId, token) => {
    let attempts = 0;
    const poll = async () => {
      try {
        // Prefer Bearer if we still have it in localStorage, otherwise
        // rely on the httpOnly cookie (axios.defaults.withCredentials=true
        // sends it automatically). Sending `Bearer null` would break the
        // header parser, so we build the config conditionally.
        const cfg = token
          ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          : { withCredentials: true };
        const res = await axios.get(`${API}/members/stripe/subscription-status/${sessionId}`, cfg);
        if (res.data.payment_status === "paid") {
          // iter127: after this member's card is saved, if we have a queue
          // of remaining adult family peers, kick off the next one.
          let queue = [];
          try { queue = JSON.parse(localStorage.getItem("member_reg_family_queue") || "[]"); } catch { queue = []; }
          if (queue.length > 0) {
            const nextMemberId = queue.shift();
            localStorage.setItem("member_reg_family_queue", JSON.stringify(queue));
            setPaymentPolling(false);
            toast.success("Card saved. Next family member in a moment…");
            await handlePayment(false, nextMemberId);
            return;
          }
          setPaymentPolling(false);
          setPaymentDone(true);
          localStorage.removeItem("member_reg_token");
          localStorage.removeItem("member_reg_fee");
          localStorage.removeItem("member_reg_is_setup");
          localStorage.removeItem("member_reg_family_queue");
          return;
        }
        if (res.data.status === "expired") {
          setPaymentPolling(false);
          toast.error("Payment session expired. Please try again.");
          setStep(7);
          return;
        }
      } catch (err) {
        console.error("Request failed:", err?.message || err);
        // 401 = both bearer + cookie are gone. Halt polling and give the
        // user a clear next step instead of silently retrying to no end.
        if (err?.response?.status === 401) {
          setPaymentPolling(false);
          toast.error("Session expired during payment. Please log in to check your application status.");
          return;
        }
      }
      attempts++;
      if (attempts < 40) setTimeout(poll, 2500);
      else {
        setPaymentPolling(false);
        toast.error("Payment verification timed out. Contact support if your payment was charged.");
      }
    };
    poll();
  }, []);

  const checkExistingGuest = useCallback(async (val) => {
    if (!val || !val.includes("@")) return;
    setCheckingEmail(true);
    try {
      const res = await axios.post(`${API}/members/check-email`, { email: val });
      if (res.data.status === "existing_guest") {
        setIsExistingGuest(true);
        setGuestVerified(false);
        // Pre-fill guest details
        if (res.data.name && !name) setName(res.data.name);
        if (res.data.address && !address) setAddress(res.data.address);
        if (res.data.mobile_number && !mobile) setMobile(res.data.mobile_number);
      } else if (res.data.status === "existing_member") {
        toast.error("This email is already registered as a member. Please log in.");
        setIsExistingGuest(false);
      } else {
        setIsExistingGuest(false);
      }
    } catch (err) { console.error("Request failed:", err?.message || err); }
    finally { setCheckingEmail(false); }
  }, []);

  useEffect(() => {
    loadMemberTypes();
    const params = new URLSearchParams(window.location.search);
    const paymentSession = params.get("payment_session");
    const cancelled = params.get("payment_cancelled");
    const prefillEmail = params.get("email");
    const savedToken = localStorage.getItem("member_reg_token");
    const savedFee = localStorage.getItem("member_reg_fee");

    // Return-from-Stripe: `payment_session` in the URL is enough to resume.
    // We deliberately DO NOT require `savedToken` in localStorage — Stripe
    // often opens in an in-app browser / new tab and the return lands
    // without the original bucket. The httpOnly `access_token` cookie
    // (7-day life, rotated on self_set_password) still authorises the
    // poll, so the flow can resume cleanly. If the cookie is also gone
    // (rare — different device, incognito), the poll returns 401 and the
    // user gets a clear "session expired" toast instead of a silent
    // reset to Step 1.
    if (paymentSession) {
      if (savedToken) setRegToken(savedToken);
      if (savedFee) setAnnualFee(parseFloat(savedFee));
      setStep(8);
      setPaymentPolling(true);
      pollPaymentStatus(paymentSession, savedToken);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (cancelled && savedToken) {
      setRegToken(savedToken);
      if (savedFee) setAnnualFee(parseFloat(savedFee));
      setStep(7);
      toast.info("Payment cancelled. You can try again.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (googlePrefill?.email) {
      // Coming from Emergent Google auth: prefill email + name (locked). The
      // user still has to complete DOB/Address/Mobile and re-verify their
      // email via OTP in Step 2 — same as any brand-new registration.
      setEmail(googlePrefill.email);
      if (googlePrefill.name) setName(googlePrefill.name);
    } else if (prefillEmail) {
      setEmail(prefillEmail);
      setEmailLockedFromSession(true);
      checkExistingGuest(prefillEmail);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loadMemberTypes, pollPaymentStatus, checkExistingGuest, googlePrefill]);

  // Load wallet balance when payment step is reached
  useEffect(() => {
    if (step === 7 && regToken) loadWalletBalance(regToken);
  }, [step, regToken, loadWalletBalance]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSendGuestOtp = async () => {
    if (!email) return toast.error("Please enter your email");
    setGuestOtpSending(true);
    try {
      await axios.post(`${API}/members/verify-guest-send-otp`, { email });
      setGuestOtpSent(true);
      toast.success("Verification code sent to your email");
    } catch (err) {
      toast.error(getErrMsg(err, "Failed to send verification code"));
    } finally { setGuestOtpSending(false); }
  };

  const handleVerifyGuest = async (e) => {
    e.preventDefault();
    if (!guestOtp) return toast.error("Please enter the verification code");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/members/verify-guest`, { email, code: guestOtp });
      setGuestVerified(true);
      if (res.data.name) setName(res.data.name);
      if (res.data.dob) setDob(res.data.dob);
      if (res.data.address) setAddress(res.data.address);
      if (res.data.mobile_number) setMobile(res.data.mobile_number);
      toast.success("Identity verified! Please review and complete your details.");
    } catch (err) {
      toast.error(getErrMsg(err, "Verification failed. Please check the code."));
    } finally { setLoading(false); }
  };

  const handleStep1Continue = async (e) => {
    e.preventDefault();
    if (!name || !dob || !address) return toast.error("Please fill all required fields (name, date of birth, address)");
    const dobErr = validateDob(dob);
    if (dobErr) return toast.error(dobErr);

    if (isExistingGuest && guestVerified) {
      setStep(3);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/members/self-register`, { name, email, dob, address, mobile_number: mobile });
      toast.success("Verification code sent to your email");
      setStep(2);
    } catch (err) {
      toast.error(getErrMsg(err, "Registration failed"));
    } finally { setLoading(false); }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    if (!code) return toast.error("Please enter the verification code");
    setLoading(true);
    try {
      await axios.post(`${API}/members/self-verify-email`, { email, code });
      toast.success("Email verified!");
      setStep(3);
    } catch (err) {
      toast.error(getErrMsg(err, "Verification failed"));
    } finally { setLoading(false); }
  };

  const handleAddFamilyMember = () => {
    if (!newFamily.name || !newFamily.dob) return toast.error("Name and date of birth are required");
    const dobErr = validateDob(newFamily.dob);
    if (dobErr) return toast.error(dobErr);
    const isAdult = calculateAge(newFamily.dob) >= 17;
    if (isAdult && !newFamily.email) return toast.error("Email is required for adult family members");
    if (newFamily.email && !newFamily.email_verified) return toast.error("Please verify the email address first");
    setFamilyMembers([...familyMembers, { ...newFamily, address: newFamily.address || address }]);
    setNewFamily({ name: "", email: "", dob: "", mobile_number: "", address: "", email_verified: false });
    setShowAddFamily(false);
    toast.success("Family member added");
  };

  const handleLinkedMember = (linked) => {
    setFamilyMembers([...familyMembers, { ...linked, linked: true, email_verified: true }]);
    setShowLinkSearch(false);
    toast.success(`${linked.name} linked to your family`);
  };

  const handleSaveEditFamily = () => {
    if (!editFamily.name || !editFamily.dob) return toast.error("Name and date of birth are required");
    const dobErr = validateDob(editFamily.dob);
    if (dobErr) return toast.error(dobErr);
    const isAdult = calculateAge(editFamily.dob) >= 17;
    if (isAdult && !editFamily.email) return toast.error("Email is required for adult family members");
    if (editFamily.email && !editFamily.email_verified) return toast.error("Please verify the email address first");
    setFamilyMembers(familyMembers.map((fm, i) => i === editFamilyIdx ? { ...editFamily, address: editFamily.address || address } : fm));
    setEditFamilyIdx(null);
    setEditFamily({ name: "", email: "", dob: "", mobile_number: "", address: "", email_verified: false });
    toast.success("Family member updated");
  };

  const startEditFamily = (idx) => {
    setEditFamilyIdx(idx);
    setEditFamily({ ...familyMembers[idx] });
    setShowAddFamily(false);
  };

  // Step 3 continue: auto-assign member type based on age, skip step 4, go to agreements
  const handleStep3Continue = () => {
    const age = calculateAge(dob);
    const childType = memberTypes.find((t) => t.name === "Child");
    const familyType = memberTypes.find((t) => t.name === "Family");
    if (age < 17 && childType) setSelectedTypeId(childType.id);
    else if (familyType) setSelectedTypeId(familyType.id);
    else if (memberTypes.length > 0) setSelectedTypeId(memberTypes[0].id);
    setStep(5);
  };

  const submitCompleteSetup = async () => {
    if (!selectedTypeId) {
      toast.error("Member type could not be determined. Please go back and try again.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/members/self-set-password`, {
        email,
        verification_code: code,
        password: isExistingGuest ? null : null,
        member_type_id: selectedTypeId,
        address: address || null,
        mobile_number: mobile || null,
        satzung_consent: satzungConsent,
        auto_subscription_consent: autoSubConsent,
        gdpr_consent: gdprConsent,
        family_members: familyMembers.map(fm => ({
          name: fm.name,
          dob: fm.dob,
          email: fm.email || null,
          address: fm.address || null,
          mobile_number: fm.mobile_number || null,
          email_verified: fm.email_verified || false,
          linked_member_id: fm.linked && fm.linked_type !== "guest" ? fm.member_id : null,
          linked_guest_id: fm.linked_guest_id || null,
        })),
      });
      localStorage.setItem("member_reg_token", res.data.token);
      localStorage.setItem("member_reg_fee", String(res.data.annual_fee));
      localStorage.setItem("member_reg_member_id", res.data.member_id);
      // iter127: stash the adult peer queue so `handlePayment` can walk
      // each one through their own Save Card after the primary completes.
      try {
        const peers = res.data.family_adult_member_ids || [];
        localStorage.setItem("member_reg_family_queue", JSON.stringify(peers));
      } catch { /* ignore */ }
      setRegToken(res.data.token);
      setAnnualFee(res.data.annual_fee);
      setMemberTypeName(res.data.member_type);
      setStep(7);
    } catch (err) {
      toast.error(getErrMsg(err, "Failed to complete setup"));
    } finally { setLoading(false); }
  };

  const handleStep5Continue = async () => {
    if (!satzungConsent || !gdprConsent || !autoSubConsent) return toast.error("Please agree to all terms");
    await submitCompleteSetup();
  };

  const handlePayment = async (useWallet = false, forMemberId = null) => {
    setLoading(true);
    try {
      // iter127: always pin the checkout to a specific member so the
      // backend takes the per-member branch (own Customer + SetupIntent +
      // Subscription) instead of the legacy fold-into-primary path. On
      // the FIRST call (no forMemberId), that member is the primary.
      const primaryMemberId = localStorage.getItem("member_reg_member_id");
      const targetMemberId = forMemberId || primaryMemberId;
      const res = await axios.post(
        `${API}/members/stripe/create-subscription-checkout`,
        {
          origin_url: window.location.origin + "/" + (window.location.pathname.split("/")[1] || "mymaitree"),
          use_wallet: useWallet,
          ...(targetMemberId ? { for_member_id: targetMemberId } : {}),
        },
        { headers: { Authorization: `Bearer ${regToken}` } }
      );
      if (res.data.no_payment_required) {
        toast.success("Account submitted for approval.");
        setPaymentDone(true);
        setStep(8);
        localStorage.removeItem("member_reg_token");
        localStorage.removeItem("member_reg_fee");
        localStorage.removeItem("member_reg_family_queue");
      } else {
        localStorage.setItem("member_reg_is_setup", res.data.is_setup ? "true" : "false");
        // pollPaymentStatus reads `member_reg_family_queue` after the
        // return-from-Stripe redirect and, if non-empty, auto-launches
        // the next peer's Save Card.
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(getErrMsg(err, "Failed to start payment"));
      setLoading(false);
    }
  };

  const handleSepaSendCode = async () => {
    if (!sepaEmail) return toast.error("Please enter your email");
    setSepaEmailLoading(true);
    try {
      await axios.post(`${API}/wallet/resend-verification`, { email: sepaEmail });
      setSepaEmailSent(true);
      toast.success("Verification code sent to your email");
    } catch {
      // If no pending registration, use the member registration verification
      try {
        await axios.post(`${API}/members/family/send-verification`, { email: sepaEmail, name: "" });
        setSepaEmailSent(true);
        toast.success("Verification code sent");
      } catch (e2) {
        toast.error("Failed to send verification code");
      }
    } finally { setSepaEmailLoading(false); }
  };

  const handleSepaVerifyCode = async () => {
    if (!sepaEmailCode) return toast.error("Enter the verification code");
    setSepaEmailLoading(true);
    try {
      await axios.post(`${API}/wallet/verify-email`, { email: sepaEmail, code: sepaEmailCode });
      setSepaEmailVerified(true);
      toast.success("Email verified!");
    } catch {
      try {
        await axios.post(`${API}/members/family/verify-email`, { email: sepaEmail, code: sepaEmailCode });
        setSepaEmailVerified(true);
        toast.success("Email verified!");
      } catch (e2) {
        toast.error(e2.response?.data?.detail || "Invalid code");
      }
    } finally { setSepaEmailLoading(false); }
  };

  const handleSepaPayment = async () => {
    if (!sepaForm.account_holder || !sepaForm.street || !sepaForm.plz_city || !sepaForm.bank_name || !sepaForm.iban) {
      return toast.error("Please fill in all required bank details");
    }
    // Existing guests are already OTP-verified in Step 1 → the member_pending
    // reg-token itself acts as the electronic signature (backend accepts it).
    // Only brand-new registrations still need the email-verify step.
    if (!isExistingGuest && !sepaEmailVerified) return toast.error("Please verify your email first");
    setSepaLoading(true);
    try {
      const memberId = localStorage.getItem("member_reg_member_id") || "";
      await axios.post(`${API}/wallet/sepa-mandate`, {
        member_id: memberId,
        signer_email: isExistingGuest ? email : sepaEmail,
        ...sepaForm,
      }, { headers: { Authorization: `Bearer ${regToken}` } });
      toast.success("SEPA mandate submitted. Pending FA approval.");
      setPaymentDone(true);
      setStep(8);
    } catch (err) {
      toast.error(getErrMsg(err, "SEPA submission failed"));
    } finally { setSepaLoading(false); }
  };


  const selectedType = memberTypes.find((t) => t.id === selectedTypeId);

  // ── Shared sub-components ─────────────────────────────────────────────────

  const UpgradeBanner = () => isExistingGuest ? (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-3 mb-5 flex gap-2 items-center">
      <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
      <p className="text-green-700 text-xs font-medium">Upgrading existing account · Your wallet balance is preserved</p>
    </div>
  ) : null;

  // ── Step 1: Personal Details ──────────────────────────────────────────────

  if (step === 1) {
    const showPasswordGate = isExistingGuest && !guestVerified;
    const showFullForm = !isExistingGuest || guestVerified;
    const _slug = window.location.pathname.split("/")[1] || "mymaitree";
    return (
      <Card visual={visual} steps={steps} onBack={handleBack} isStep1>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-1">
          {isExistingGuest ? "Upgrade to Member" : "Join Maitree e.V."}
        </h2>
        <p className="text-slate-500 text-sm mb-5">
          Step 1 of {steps.length}: {isExistingGuest ? "Verify your account" : "Personal Details"}
        </p>

        {!isExistingGuest && !googlePrefill && (
          <>
            <GoogleAuthButton tenantSlug={_slug} label="Continue with Google (faster)" intent="member" />
            <p className="text-[11px] text-slate-400 mt-2 text-center">Google pre-fills your name and email. You still verify your email with a one-time code and complete your profile below.</p>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">or continue with email</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
          </>
        )}
        {googlePrefill && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 mb-5 flex gap-2 items-center">
            <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <p className="text-blue-700 text-xs font-medium">Signed in with Google as <strong>{googlePrefill.email}</strong>. Complete your profile and verify your email with a one-time code to submit your application.</p>
          </div>
        )}

        {/* Email field (always shown) */}
        <div className="mb-4">
          <label className="text-sm font-medium text-slate-600 mb-1 block">
            Email Address *
            {emailLockedFromSession && !googlePrefill && <span className="text-blue-600 text-xs ml-2">(signed in)</span>}
            {googlePrefill && <span className="text-blue-600 text-xs ml-2">(from Google)</span>}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
            <input
              value={email}
              onChange={e => { setEmail(e.target.value); setIsExistingGuest(false); setGuestVerified(false); }}
              onBlur={() => checkExistingGuest(email)}
              disabled={guestVerified || !!googlePrefill || emailLockedFromSession}
              required
              type="email"
              placeholder="your@email.com"
              className="w-full pl-10 pr-10 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="member-email-input"
            />
            {checkingEmail && <Loader2 className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 animate-spin" />}
            {guestVerified && <ShieldCheck className="absolute right-3 top-3.5 w-4 h-4 text-green-500" />}
          </div>
        </div>

        {/* OTP identity gate for existing guests (works for password + Google auth) */}
        {showPasswordGate && (
          <>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4 flex gap-3 items-start">
              <Heart className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-green-800 text-sm">Welcome! You have an existing guest account.</p>
                <p className="text-green-700 text-xs mt-0.5">We&apos;ll send a one-time code to your email to verify it&apos;s you. Your wallet balance and history will be preserved.</p>
              </div>
            </div>
            <form onSubmit={handleVerifyGuest} className="space-y-4">
              {!guestOtpSent ? (
                <button
                  type="button"
                  onClick={handleSendGuestOtp}
                  disabled={guestOtpSending}
                  className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-guest-otp-btn"
                >
                  {guestOtpSending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Send verification code to {email || "my email"}
                </button>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-600 mb-1 block">Verification code *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                      <input
                        value={guestOtp}
                        onChange={e => setGuestOtp(e.target.value.trim())}
                        type="text"
                        inputMode="numeric"
                        maxLength={8}
                        placeholder="6-digit code from your email"
                        required
                        autoComplete="one-time-code"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] tracking-widest text-center font-mono"
                        data-testid="guest-otp-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSendGuestOtp}
                      disabled={guestOtpSending}
                      className="text-xs text-[#1e3a5f] hover:underline mt-1 disabled:opacity-50"
                      data-testid="resend-guest-otp-btn"
                    >{guestOtpSending ? "Sending..." : "Resend code"}</button>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="verify-guest-btn"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Verify My Account
                  </button>
                </>
              )}
            </form>
          </>
        )}

        {/* Full personal details form */}
        {showFullForm && (
          <>
            {guestVerified && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-3 mb-4 flex gap-2 items-center">
                <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                <p className="text-green-700 text-xs font-medium">Identity verified. Your details have been pre-filled — review and complete any missing fields.</p>
              </div>
            )}
            <form onSubmit={handleStep1Continue} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">
                  Full Name *
                  {guestVerified && name && <span className="text-green-600 text-xs ml-2">(from guest account)</span>}
                  {googlePrefill?.name && !guestVerified && <span className="text-blue-600 text-xs ml-2">(from Google)</span>}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    disabled={(guestVerified && !!name) || !!googlePrefill?.name}
                    placeholder="Your full name"
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] ${((guestVerified && name) || googlePrefill?.name) ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                    data-testid="member-name-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">
                  Date of Birth *
                  {guestVerified && dob && <span className="text-green-600 text-xs ml-2">(from guest account)</span>}
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                  <input
                    value={dob}
                    onChange={e => setDob(e.target.value)}
                    required
                    disabled={guestVerified && !!dob}
                    type="date"
                    max={TODAY}
                    min={MIN_DOB}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] ${guestVerified && dob ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                    data-testid="member-dob-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Address *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <textarea
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    required
                    placeholder="Street, City, Postal Code, Country"
                    rows={2}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] resize-none"
                    data-testid="member-address-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">
                  Mobile Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                  <input
                    value={mobile}
                    onChange={e => setMobile(e.target.value)}
                    type="tel"
                    placeholder="+49 123 456 7890"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
                    data-testid="member-mobile-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="member-step1-submit"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Continue
              </button>
            </form>
            {!isExistingGuest && (
              <p className="text-center text-sm text-slate-400 mt-4">
                Already a member?{" "}
                <a href={`/${(window.location.pathname.split("/")[1] || "mymaitree")}/login`} className="text-[#1e3a5f] font-bold hover:underline">Login</a>
              </p>
            )}
          </>
        )}
      </Card>
    );
  }

  // ── Step 2: Email Verification ────────────────────────────────────────────

  if (step === 2) return (
    <Card visual={visual} steps={steps} onBack={handleBack}>
      <h2 className="text-2xl font-bold text-[#1e3a5f] mb-1">Verify Your Email</h2>
      <p className="text-slate-500 text-sm mb-5">
        Step 2 of {steps.length}: Check <strong>{email}</strong> for a 6-digit code
      </p>
      <form onSubmit={handleStep2} className="space-y-4">
        <input
          value={code}
          onChange={e => setCode(e.target.value)}
          required
          placeholder="------"
          maxLength={6}
          className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] text-center text-2xl tracking-[0.5em] font-mono"
          data-testid="member-otp-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="member-step2-submit"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Verify Email
        </button>
      </form>
    </Card>
  );

  // ── Step 3: Family Members ────────────────────────────────────────────────

  if (step === 3) return (
    <Card visual={visual} steps={steps} onBack={handleBack}>
      <h2 className="text-2xl font-bold text-[#1e3a5f] mb-1">Family Members</h2>
      <p className="text-slate-500 text-sm mb-5">
        Step {visual} of {steps.length}: Optionally add family members to your membership
      </p>
      <UpgradeBanner />

      {/* Existing family members list */}
      {familyMembers.length > 0 && (
        <div className="mb-4 space-y-2">
          {familyMembers.map((fm, i) => (
            <div key={i}>
              {editFamilyIdx === i ? (
                <FamilyForm
                  data={editFamily}
                  setData={setEditFamily}
                  onConfirm={handleSaveEditFamily}
                  onCancel={() => {
                    setEditFamilyIdx(null);
                    setEditFamily({ name: "", email: "", dob: "", mobile_number: "", address: "", email_verified: false });
                  }}
                  confirmLabel="Save"
                  mainAddress={address}
                />
              ) : fm.linked ? (
                <div className={`rounded-xl p-3 border ${fm.linked_type === "guest" ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className={`font-medium text-sm ${fm.linked_type === "guest" ? "text-amber-800" : "text-green-800"}`}>{fm.name}</p>
                    <button
                      onClick={() => {
                        setFamilyMembers(familyMembers.filter((_, j) => j !== i));
                      }}
                      className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                      data-testid={`remove-family-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
                    {fm.email && <p>Email: {fm.email}</p>}
                    {fm.dob && <p>DOB: {fm.dob}</p>}
                    {fm.address && <p className="col-span-2">Address: {fm.address}{fm.inherited_address ? " (inherited from you)" : ""}</p>}
                    {fm.mobile_number && <p>Mobile: {fm.mobile_number}{fm.inherited_mobile ? " (inherited from you)" : ""}</p>}
                  </div>
                  {fm.linked_type === "guest" ? (
                    <p className="text-xs text-amber-700 font-medium mt-1" data-testid={`linked-guest-note-${i}`}>
                      Linked existing guest — will be upgraded to family member on submit
                    </p>
                  ) : (
                    <p className="text-xs text-green-600 font-medium mt-1">
                      Linked existing member{fm.last_membership_fee_paid_date ? ` — last fee paid: ${fm.last_membership_fee_paid_date}` : ""}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{fm.name}</p>
                    <p className="text-xs text-slate-500">
                      {fm.dob ? `Born ${fm.dob}` : ""}
                      {fm.email ? ` \u00b7 ${fm.email}` : ""}
                      {fm.email_verified ? " \u2713" : ""}
                      {fm.dob ? ` \u00b7 ${calculateAge(fm.dob) < 17 ? "Child (free)" : "Adult (Family fee)"}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEditFamily(i)}
                      className="p-1.5 rounded-lg text-[#1e3a5f] hover:bg-blue-50 transition-colors"
                      data-testid={`edit-family-${i}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (editFamilyIdx === i) { setEditFamilyIdx(null); }
                        setFamilyMembers(familyMembers.filter((_, j) => j !== i));
                      }}
                      className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                      data-testid={`remove-family-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add family member form or button */}
      {showAddFamily && editFamilyIdx === null ? (
        <FamilyForm
          data={newFamily}
          setData={setNewFamily}
          onConfirm={handleAddFamilyMember}
          onCancel={() => {
            setShowAddFamily(false);
            setNewFamily({ name: "", email: "", dob: "", mobile_number: "", address: "", email_verified: false });
          }}
          confirmLabel="Add"
          mainAddress={address}
        />
      ) : showLinkSearch && editFamilyIdx === null ? (
        <SearchLinkForm
          onLinked={handleLinkedMember}
          onCancel={() => setShowLinkSearch(false)}
          mainAddress={address}
          mainMobile={mobile}
        />
      ) : editFamilyIdx === null ? (
        <div className="space-y-2 mb-4">
          <button
            onClick={() => { setShowAddFamily(true); setShowLinkSearch(false); }}
            className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 font-medium text-sm flex items-center justify-center gap-2 hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-colors"
            data-testid="show-add-family-btn"
          >
            <Plus className="w-4 h-4" /> Add a new family member
          </button>
          <button
            onClick={() => { setShowLinkSearch(true); setShowAddFamily(false); }}
            className="w-full py-3 rounded-xl border-2 border-dashed border-blue-300 text-blue-500 font-medium text-sm flex items-center justify-center gap-2 hover:border-blue-600 hover:text-blue-600 transition-colors"
            data-testid="show-link-member-btn"
          >
            <Heart className="w-4 h-4" /> Link an existing Maitree member
          </button>
        </div>
      ) : null}

      <p className="text-xs text-slate-400 mb-5">
        Adults (17+) must provide a verified email. Children (&lt;17) are free and email is optional.
      </p>

      <button
        onClick={handleStep3Continue}
        className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold"
        data-testid="member-step3-continue"
      >
        Continue {familyMembers.length > 0 ? `(${familyMembers.length} member${familyMembers.length > 1 ? "s" : ""} added)` : "(skip)"}
      </button>
    </Card>
  );

  // Step 4 is intentionally removed — member type auto-assigned in handleStep3Continue

  // ── Step 5: Agreements (then straight to payment — no password step) ──────

  if (step === 5) {
    // Compute fee breakdown per member
    const familyType = memberTypes.find(t => t.name === "Family");
    const childType = memberTypes.find(t => t.name === "Child");
    const mainFee = selectedType?.annual_fee || 0;
    const feeBreakdown = [
      { label: name || "You", type: selectedType?.name || "Family", fee: mainFee },
      ...familyMembers.map(fm => {
        const isChild = calculateAge(fm.dob) < 17;
        const fee = isChild ? (childType?.annual_fee || 0) : (familyType?.annual_fee || mainFee);
        return { label: fm.name, type: isChild ? "Child" : "Family", fee };
      }),
    ];
    const totalFee = feeBreakdown.reduce((sum, item) => sum + item.fee, 0);

    return (
    <Card visual={visual} steps={steps} onBack={handleBack}>
      <h2 className="text-2xl font-bold text-[#1e3a5f] mb-1">Terms & Agreements</h2>
      <p className="text-slate-500 text-sm mb-5">
        Step {visual} of {steps.length}: Please read and agree to all terms
      </p>
      <UpgradeBanner />

      {/* Fee breakdown per member */}
      <div className="bg-blue-50 rounded-xl p-4 mb-5">
        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Membership Fees</p>
        <div className="space-y-1">
          {feeBreakdown.map((item, i) => (
            <div key={i} className="flex justify-between text-sm text-blue-800">
              <span>{item.label} <span className="text-blue-500 text-xs">({item.type})</span></span>
              <span className="font-medium">{item.fee > 0 ? `\u20ac${item.fee}/yr` : "Free"}</span>
            </div>
          ))}
        </div>
        {feeBreakdown.length > 1 && (
          <div className="flex justify-between text-sm font-bold text-blue-900 border-t border-blue-200 pt-2 mt-2">
            <span>Total per year</span>
            <span>{"\u20ac"}{totalFee}</span>
          </div>
        )}
      </div>

      {/* Info: password will be sent after FA approval */}
      {!isExistingGuest && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5">
          <p className="text-xs text-blue-700">A temporary password will be sent to your email after your membership is approved by the Finance Admin. You'll change it on first login.</p>
        </div>
      )}

      <div className="space-y-3 mb-6">
        {[
          {
            state: satzungConsent, set: setSatzungConsent, testid: "satzung-consent",
            title: "I agree to the Satzung (Bylaws) *",
            body: <span>I have read and accept the <a href="https://maitree-ev.org/satzung/" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline" onClick={e => e.stopPropagation()}>Satzung of Maitree e.V.</a></span>
          },
          {
            state: gdprConsent, set: setGdprConsent, testid: "gdpr-consent",
            title: "I agree to the GDPR Privacy Policy *",
            body: <span>I have read and accept the <a href="https://maitree-ev.org/privacy-statement-eu/?cmplz_region_redirect=true" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline" onClick={e => e.stopPropagation()}>Privacy Policy</a> and consent to Maitree e.V. storing and processing my personal data in accordance with GDPR.</span>
          },
          {
            state: autoSubConsent, set: setAutoSubConsent, testid: "auto-sub-consent",
            title: "I agree to the annual subscription *",
            body: totalFee > 0
              ? `I agree to pay \u20ac${totalFee} annually for ${feeBreakdown.length} member${feeBreakdown.length > 1 ? "s" : ""} and authorise recurring payments.`
              : "I understand this is a fee-exempt membership."
          },
        ].map(({ state, set, testid, title, body }) => (
          <label
            key={testid}
            className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${state ? "border-green-400 bg-green-50" : "border-slate-200 hover:border-slate-300"}`}
          >
            <input
              type="checkbox"
              checked={state}
              onChange={e => set(e.target.checked)}
              className="mt-0.5 accent-green-500 w-4 h-4 flex-shrink-0"
              data-testid={testid}
            />
            <div>
              <p className="font-semibold text-sm text-slate-700">{title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{body}</p>
            </div>
          </label>
        ))}
      </div>

      <button
        onClick={handleStep5Continue}
        disabled={!satzungConsent || !gdprConsent || !autoSubConsent || loading}
        className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="member-step5-continue"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Proceed to Payment
      </button>
    </Card>
    );
  }

  // ── Step 7: Payment ───────────────────────────────────────────────────────

  if (step === 7) {
    const stripeFullFee = computeStripeFee(annualFee);
    const stripeFull = Math.round((annualFee + stripeFullFee) * 100) / 100;

    return (
      <Card visual={visual} steps={steps} onBack={handleBack}>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-1">Subscription Fee</h2>
        <p className="text-slate-500 text-sm mb-5">
          Step {steps.length} of {steps.length}: Complete your membership
        </p>
        <UpgradeBanner />

        {/* Fee summary card */}
        <div className="bg-[#1e3a5f] text-white rounded-2xl p-6 mb-5 text-center">
          <p className="text-sm text-white/60 mb-1">Annual Membership Fee</p>
          <p className="text-4xl font-extrabold mb-1">
            {annualFee > 0 ? `\u20ac${annualFee.toFixed(2)}` : "Free"}
          </p>
          <p className="text-white/60 text-sm">{memberTypeName || "Family"} membership · auto-renews annually</p>
        </div>

        {annualFee > 0 ? (
          <>
            {/* Stripe subscription payment */}
            <div>
              <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Membership fee</span>
                  <span className="font-medium">{"\u20ac"}{annualFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-slate-500">Processing fee ({chargesLabel})</span>
                  <span className="font-medium">{"\u20ac"}{stripeFullFee.toFixed(2)}</span>
                </div>
                <p className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="member-fee-nonrefundable-note">
                  <Info className="w-3 h-3" /> Note: Processing fees are non-refundable
                </p>
                <div className="flex justify-between border-t pt-2 font-bold text-slate-800">
                  <span>Charged upon FA approval</span>
                  <span>{"\u20ac"}{stripeFull.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-slate-500">Renewal</span>
                  <span className="font-medium">{"\u20ac"}{stripeFull.toFixed(2)}/year · auto</span>
                </div>
              </div>
              <button
                onClick={() => handlePayment(false)}
                disabled={loading}
                className="w-full py-4 rounded-xl bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="pay-subscription-btn"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Card &amp; Submit Application
              </button>
              <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5" data-testid="setup-first-reassurance">
                <p className="text-xs text-emerald-800 font-semibold flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Nothing charged yet
                </p>
                <p className="text-[11px] text-emerald-700/90 mt-1 leading-snug">
                  We&apos;ll only bill {"\u20ac"}{stripeFull.toFixed(2)} once a Finance Admin approves your application. If your application is rejected, nothing is charged. Your card details are handled and stored by Stripe &mdash; Maitree e.V. never sees or saves them.
                </p>
              </div>
            </div>

            <p className="text-center text-xs text-slate-400 mt-3">
              Secured by Stripe · Card saved for annual auto-renewal
            </p>

            {/* SEPA Direct Debit Option */}
            <div className="border-t mt-5 pt-4">
              <button
                onClick={() => setShowSepa(!showSepa)}
                className="w-full text-left flex items-center justify-between py-2 text-sm font-medium text-[#1e3a5f]"
                data-testid="sepa-toggle-btn"
              >
                <span>Pay via SEPA Direct Debit (no processing fees)</span>
                {showSepa ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showSepa && (
                <div className="bg-blue-50 rounded-xl p-4 mt-2 space-y-3">
                  {/* Read-only creditor info */}
                  <div className="bg-white rounded-lg p-3 text-xs text-slate-600 space-y-0.5">
                    <p className="font-bold text-[#1e3a5f] text-sm">Zahlungsempfänger (Gläubiger)</p>
                    <p>Maitree e.V., Württembergstr 46, 73733 Esslingen am Neckar</p>
                    <p>Gläubiger-ID: DE89ZZZ00002524344</p>
                    <p>Mandatsreferenz: Maitree e.V. Beitrag</p>
                    <p className="italic mt-1">Wiederkehrende Zahlungen / Recurrent Payments</p>
                  </div>

                  <p className="text-xs text-slate-500">Ich/Wir ermächtige(n) Maitree e.V., Zahlungen von meinem/unserem Konto mittels Lastschrift einzuziehen.</p>

                  {/* User fields */}
                  <input value={sepaForm.account_holder} onChange={e => setSepaForm({...sepaForm, account_holder: e.target.value})} placeholder="Kontoinhaber (Vorname, Name) *" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="sepa-holder" />
                  <input value={sepaForm.street} onChange={e => setSepaForm({...sepaForm, street: e.target.value})} placeholder="Straße, Hausnummer *" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="sepa-street" />
                  <input value={sepaForm.plz_city} onChange={e => setSepaForm({...sepaForm, plz_city: e.target.value})} placeholder="PLZ, Ort *" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="sepa-plz" />
                  <input value={sepaForm.bank_name} onChange={e => setSepaForm({...sepaForm, bank_name: e.target.value})} placeholder="Kreditinstitut (Bank) *" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="sepa-bank" />
                  <input value={sepaForm.iban} onChange={e => setSepaForm({...sepaForm, iban: e.target.value.toUpperCase()})} placeholder="IBAN *" className="w-full px-3 py-2 rounded-lg border text-sm font-mono" data-testid="sepa-iban" />
                  <input value={sepaForm.bic} onChange={e => setSepaForm({...sepaForm, bic: e.target.value.toUpperCase()})} placeholder="BIC (optional ab 01.02.2016 innerhalb EU/EWR)" className="w-full px-3 py-2 rounded-lg border text-sm font-mono" data-testid="sepa-bic" />

                  <div className="text-xs text-slate-500 italic">
                    Hinweis: Innerhalb von acht Wochen kann die Erstattung des belasteten Betrags verlangt werden.
                  </div>

                  {/* Electronic signature */}
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <p className="text-xs font-bold text-[#1e3a5f] mb-2">Elektronische Unterschrift</p>
                    {isExistingGuest ? (
                      <>
                        <div className="mb-2">
                          <p className="text-xs text-slate-500 mb-1">E-Mail</p>
                          <input value={email} disabled className="w-full px-3 py-2 rounded-lg border text-sm bg-slate-50 text-slate-600" />
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex items-start gap-2">
                          <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-green-700">Identität bereits per OTP verifiziert / Identity already verified via OTP.</p>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500">E-Mail-Bestätigung / Email confirmation</p>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            value={sepaEmail}
                            onChange={e => { setSepaEmail(e.target.value); setSepaEmailVerified(false); setSepaEmailSent(false); setSepaEmailCode(""); }}
                            placeholder="E-Mail erneut eingeben"
                            className="flex-1 px-3 py-2 rounded-lg border text-sm"
                            disabled={sepaEmailVerified}
                            data-testid="sepa-email"
                          />
                          {!sepaEmailVerified ? (
                            <button onClick={handleSepaSendCode} disabled={sepaEmailLoading || !sepaEmail} className="px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-medium disabled:opacity-50 whitespace-nowrap" data-testid="sepa-send-code">
                              {sepaEmailLoading ? "..." : sepaEmailSent ? "Erneut" : "Code senden"}
                            </button>
                          ) : (
                            <span className="px-3 py-2 text-green-600 text-xs font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Bestätigt</span>
                          )}
                        </div>
                        {sepaEmailSent && !sepaEmailVerified && (
                          <div className="flex gap-2">
                            <input value={sepaEmailCode} onChange={e => setSepaEmailCode(e.target.value)} placeholder="6-stelliger Code" maxLength={6} className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono tracking-wider" data-testid="sepa-email-code" />
                            <button onClick={handleSepaVerifyCode} disabled={sepaEmailLoading} className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50" data-testid="sepa-verify-code">
                              {sepaEmailLoading ? "..." : "Bestätigen"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-green-50 rounded-lg p-2 text-xs text-green-700 text-center font-medium">
                    Keine Bearbeitungsgebühren / No processing fees · {"\u20ac"}{annualFee.toFixed(2)}/Jahr
                  </div>

                  <button
                    onClick={handleSepaPayment}
                    disabled={sepaLoading || (!isExistingGuest && !sepaEmailVerified)}
                    className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="sepa-submit-btn"
                  >
                    {sepaLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    SEPA-Mandat unterschreiben & einreichen
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="bg-green-50 rounded-xl p-4 mb-5">
              <p className="text-green-700 text-sm text-center font-medium">
                Your membership type is fee-exempt. No payment required.
              </p>
            </div>
            <button
              onClick={() => handlePayment(false)}
              disabled={loading}
              className="w-full py-4 rounded-xl bg-green-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-for-approval-btn"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit for Approval
            </button>
          </>
        )}
      </Card>
    );
  }

  // ── Step 8: Confirmation ──────────────────────────────────────────────────

  if (step === 8) return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-[#2d5a8f] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 text-center shadow-xl">
        {paymentPolling ? (
          <>
            <Loader2 className="w-16 h-16 text-[#1e3a5f] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Verifying Payment...</h2>
            <p className="text-slate-500 text-sm">Please wait. Do not close this page.</p>
          </>
        ) : paymentDone ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Registration Complete!</h2>
            <p className="text-slate-600 text-sm mb-4">
              Thank you for joining Maitree e.V.! Your application is pending Finance Admin approval.
            </p>
            <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-amber-700 text-sm font-semibold mb-2">What happens next?</p>
              <ul className="text-amber-600 text-sm space-y-1 list-disc list-inside">
                <li>A Finance Admin will review your application</li>
                <li>You'll receive an email once approved</li>
                <li>Set your password on your first login after approval</li>
              </ul>
            </div>
            <button
              onClick={() => navigate("/" + (window.location.pathname.split("/")[1] || "mymaitree") )}
              className="w-full py-3.5 rounded-xl bg-[#1e3a5f] text-white font-bold"
              data-testid="back-to-portal-btn"
            >
              Back to Portal
            </button>
          </>
        ) : (
          <Loader2 className="w-16 h-16 text-slate-300 mx-auto animate-spin" />
        )}
      </div>
    </div>
  );

  return null;
}
